/**
 * 확장 계측 (ADR 0024)
 * --------------------------------------------------------------------------
 * 라벨은 **닫힌 집합**입니다 — 확장 id는 이 빌드의 매니페스트 id만, 이벤트는 아래
 * 목록만 허용합니다. 그래서 시계열 수가 (확장 수 + 1) × 이벤트 수로 상수입니다.
 * 알 수 없는 id는 거절하고 고정 라벨 `unknown`으로 접습니다 — 확장이 자유 문자열을
 * 계측에 실어 카디널리티를 폭발시키거나 사용자 데이터를 흘릴 수 없습니다.
 *
 * 이 모듈은 네트워크를 하지 않습니다. 값은 프로세스 안의 유계 카운터이고,
 * 진단과 테스트가 읽습니다.
 */
import { EXTENSIONS } from "./registry";

export const EXTENSION_TELEMETRY_EVENTS = [
  "load_started",
  "load_succeeded",
  "load_timeout",
  "load_failed",
  "render_failed",
  "disabled",
] as const;

export type ExtensionTelemetryEvent = (typeof EXTENSION_TELEMETRY_EVENTS)[number];

/** 알 수 없는 확장 id가 접히는 고정 라벨. */
export const UNKNOWN_EXTENSION_LABEL = "unknown";

const counters = new Map<string, number>();

function known(id: string): boolean {
  return EXTENSIONS.some((manifest) => manifest.id === id);
}

/** 계측 라벨. 등록된 확장 id가 아니면 고정 라벨 하나로 접습니다. */
export function extensionTelemetryLabel(id: string): string {
  return known(id) ? id : UNKNOWN_EXTENSION_LABEL;
}

/**
 * 이벤트 하나를 셉니다. 등록된 id이면 true, 접힌 경우 false입니다 —
 * 호출부가 "기록되긴 했으나 알 수 없는 id였다"를 구분할 수 있습니다.
 */
export function recordExtensionEvent(id: string, event: ExtensionTelemetryEvent): boolean {
  if (!EXTENSION_TELEMETRY_EVENTS.includes(event)) return false;
  const label = extensionTelemetryLabel(id);
  const key = `${label}:${event}`;
  counters.set(key, (counters.get(key) ?? 0) + 1);
  return label === id;
}

/** 진단·테스트용 스냅숏. 키는 `확장라벨:이벤트`입니다. */
export function extensionTelemetrySnapshot(): Record<string, number> {
  return Object.fromEntries([...counters.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
}

export function resetExtensionTelemetry() {
  counters.clear();
}
